import { useEffect, useState } from "react";
import type { PanelName, SideTab } from "../../core/store";
import { useBreakpoint, useDraw, useInfo, useLabels, useMapCore, useMapStore, usePanel } from "../hooks";
import { Button } from "../primitives/Button";
import { Drawer } from "../primitives/Drawer";
import { Attribution } from "./Attribution";
import { BottomSheet } from "./BottomSheet";
import type { SheetSnap } from "./BottomSheet";
import { CoordinateReadout } from "./CoordinateReadout";
import { DrawToolbar } from "./DrawToolbar";
import { FeatureInspector } from "./FeatureInspector";
import { HintStrip } from "./HintStrip";
import { LayerDrawer } from "./LayerDrawer";
import { LayerTablePanel } from "./LayerTablePanel";
import { Legend } from "./Legend";
import { LocateButton } from "./LocateButton";
import { MeasureReadout } from "./MeasureReadout";
import { MiniMap } from "./MiniMap";
import { OverlaySlots } from "./OverlaySlots";
import { PanelSearch } from "./PanelSearch";
import { ResultsPane } from "./ResultsPane";
import { ScaleBar } from "./ScaleBar";
import { SelectionPill } from "./SelectionPill";
import { SidePanel, SidePanelOpener } from "./SidePanel";
import { Toasts } from "./Toasts";
import { ToolStack } from "./ToolStack";
import { ZoomControl } from "./ZoomControl";

export function sheetTabFor(panel: PanelName, infoOpen: boolean, controls: { layersTab: boolean; search: { enabled: boolean } } = { layersTab: true, search: { enabled: true } }): SideTab {
  if (panel === "search" && controls.search.enabled) return "results";
  if (panel === "info" || infoOpen || !controls.layersTab) return "selected";

  return "layers";
}

function LegendPanel() {
  const labels = useLabels();
  const { panel, setPanel } = usePanel();

  return (
    <Drawer open={panel === "legend"} title={labels.legend} side="left" role="dialog" onClose={() => setPanel(null)} closeLabel={labels.close} dataFxmap="legend-panel">
      <Legend />
    </Drawer>
  );
}

function CompactLayersButton() {
  const labels = useLabels();
  const { panel, toggle } = usePanel();

  return <Button kind="icon" size="lg" icon="layers" label={labels.layers} className="fxmap-card fxmap-compact-layers" data-fxmap="layer-toggle" pressed={panel === "layers"} aria-haspopup="dialog" aria-expanded={panel === "layers"} onClick={() => toggle("layers")} />;
}

export function CompactChrome() {
  const core = useMapCore();
  const { snapshot } = useDraw();
  const controls = core.options.controls;

  return (
    <OverlaySlots
      left={
        <>
          {controls.drawer ? <CompactLayersButton /> : null}
          {controls.drawer ? <LayerDrawer /> : null}
        </>
      }
      top={
        <>
          {controls.hint ? <HintStrip /> : null}
          {controls.drawToolbar || snapshot.geometry.mode !== null ? <DrawToolbar showWhenIdle={controls.drawToolbar} /> : null}
        </>
      }
      right={<FeatureInspector variant="popup" />}
      bottomCentre={
        <>
          <SelectionPill />
          <Toasts />
        </>
      }
      bottomRight={
        <>
          {controls.attribution ? <Attribution /> : null}
          {controls.zoom ? <ZoomControl /> : null}
        </>
      }
    />
  );
}

export function PhoneChrome() {
  const core = useMapCore();
  const labels = useLabels();
  const { panel, setPanel } = usePanel();
  const { state } = useInfo();
  const { snapshot } = useDraw();
  const tab = useMapStore(a => a.sideTab);
  const resultCount = useMapStore(a => a.results.items.length);
  const controls = core.options.controls;
  const infoOpen = state.kind !== "closed";
  const [snap, setSnap] = useState<SheetSnap>("peek");

  useEffect(() => {
    if (infoOpen) setSnap(previous => (previous === "peek" ? "half" : previous));
  }, [infoOpen, state.kind]);

  useEffect(() => {
    if (resultCount > 0) setSnap(previous => (previous === "peek" ? "half" : previous));
  }, [resultCount]);

  useEffect(() => {
    if (panel === null) return;

    core.store.getState().patch({ sideTab: sheetTabFor(panel, core.store.getState().info.kind !== "closed", core.options.controls) });
    setSnap(previous => (previous === "peek" ? "half" : previous));
  }, [core, panel]);

  const openTab = (next: SideTab) => {
    core.store.getState().patch({ sideTab: next });
    setSnap(previous => (previous === "peek" ? "half" : previous));
  };

  return (
    <OverlaySlots
      top={
        <>
          {controls.hint ? <HintStrip /> : null}
          {controls.drawToolbar || snapshot.geometry.mode !== null ? <DrawToolbar showWhenIdle={controls.drawToolbar} /> : null}
        </>
      }
      right={
        <div className="fxmap-fab-column" data-fxmap="fab-column">
          {controls.drawer && controls.layersTab ? <Button kind="icon" size="lg" icon="layers" label={labels.layers} className="fxmap-fab fxmap-card" data-fxmap="layer-toggle" pressed={tab === "layers" && snap !== "peek"} onClick={() => openTab("layers")} /> : null}
          {controls.search.enabled ? <Button kind="icon" size="lg" icon="search" label={labels.search} className="fxmap-fab fxmap-card" data-fxmap="search-toggle" pressed={tab === "results" && snap !== "peek"} onClick={() => openTab("results")} /> : null}
          {controls.locate ? <LocateButton className="fxmap-fab fxmap-card" /> : null}
          {controls.zoom ? <ZoomControl className="fxmap-fab-zoom" /> : null}
        </div>
      }
      bottomCentre={
        <>
          <MeasureReadout />
          <SelectionPill />
          <Toasts />
        </>
      }
    >
      <BottomSheet<SideTab>
        tabs={[
          ...(controls.layersTab ? [{ id: "layers" as SideTab, label: labels.layers, icon: "layers" as const }] : []),
          ...(controls.search.enabled ? [{ id: "results" as SideTab, label: labels.resultsTab, icon: "search" as const, badge: resultCount > 0 ? resultCount : null }] : []),
          { id: "selected" as SideTab, label: labels.selectedObject, icon: "info" as const }
        ]}
        tab={(tab === "layers" && !controls.layersTab) || (tab === "results" && !controls.search.enabled) ? "selected" : tab}
        onTabChange={openTab}
        snap={snap}
        onSnapChange={next => {
          setSnap(next);

          if (next === "peek") setPanel(null);
        }}
        label={labels.panelTitle}
        tabsLabel={labels.panelTitle}
        handleLabel={labels.openPanel}
        header={controls.search.enabled ? <PanelSearch /> : undefined}
      >
        {active => (active === "layers" ? <LayerDrawer embedded /> : active === "results" ? <ResultsPane /> : <FeatureInspector variant="embedded" />)}
      </BottomSheet>
    </OverlaySlots>
  );
}

export function DesktopChrome({ narrow }: { narrow: boolean }) {
  const core = useMapCore();
  const { snapshot } = useDraw();
  const controls = core.options.controls;

  return (
    <OverlaySlots
      left={controls.legend ? <LegendPanel /> : null}
      top={
        <>
          {controls.hint ? <HintStrip /> : null}
          {controls.drawToolbar || snapshot.geometry.mode !== null ? <DrawToolbar showWhenIdle={controls.drawToolbar} /> : null}
        </>
      }
      right={
        <>
          <SidePanelOpener />
          {controls.drawer ? <SidePanel /> : null}
        </>
      }
      bottomLeft={
        <>
          <ToolStack />
          {controls.zoom ? <ZoomControl /> : null}
          {controls.miniMap && !narrow ? <MiniMap /> : null}
        </>
      }
      bottomCentre={
        <>
          <MeasureReadout />
          <div className="fxmap-status-row">
            {controls.scale ? <ScaleBar /> : null}
            {controls.coordinates ? <CoordinateReadout /> : null}
          </div>
          <SelectionPill />
          <Toasts />
        </>
      }
      bottomRight={controls.attribution ? <Attribution /> : null}
    >
      <LayerTablePanel />
    </OverlaySlots>
  );
}

export function DefaultChrome() {
  const core = useMapCore();
  const breakpoint = useBreakpoint();
  const chrome = core.options.chrome;

  if (chrome === "none") return null;
  if (chrome === "compact") return <CompactChrome />;
  if (breakpoint === "phone") return <PhoneChrome />;

  return <DesktopChrome narrow={breakpoint === "narrow"} />;
}

export const defaultChrome = { DefaultChrome, DesktopChrome, PhoneChrome, CompactChrome, sheetTabFor };
